// Pure transform: structured AI Architect plan data -> React Flow
// {nodes, edges}. Deliberately derived from the plan's own structured JSON
// (tables/roles/ACLs/business rules/scheduled jobs/ui actions) rather than
// a separate LLM-drawn diagram, so the picture can never drift from what's
// actually going to be built — see the AI Architect Phase "Friendly Mode"
// plan for why.

import dagre from "dagre";

const EMAIL_PATTERN = /\bsmtp\b|\bemail\b|\bsendmail\b/i;

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

// Accepts either the raw blueprint shape (tables_needed/fields_needed/...)
// or the assembled_blueprint shape (metadata_changes.tables_to_create/...)
// and normalizes both into one consistent shape the graph builder uses.
// Falls back gracefully field-by-field so the diagram can render
// progressively as build steps complete, not only once everything exists.
export function normalizePlanState(source) {
  const data = source || {};

  const assembledTables = safeArray(data?.metadata_changes?.tables_to_create).map((t) => ({
    name: t.table_name || t.name,
    label: t.label || t.table_name || t.name,
    fields: safeArray(t.fields).map((f) => ({
      name: f.field_name || f.name,
      label: f.label || f.field_name || f.name,
      type: f.type,
    })),
  }));

  const blueprintTables = safeArray(data.tables_needed).map((t) => {
    const name = typeof t === "string" ? t : t.name || t.table_name;
    return {
      name,
      label: (typeof t === "object" && t.label) || name,
      fields: safeArray(data.fields_needed)
        .filter((f) => (f.table_name || f.table) === name)
        .map((f) => ({ name: f.name, label: f.label || f.name, type: f.type })),
    };
  });

  const tables = assembledTables.length > 0 ? assembledTables : blueprintTables;

  const roles = safeArray(data.role_changes || data.roles_needed).map((r) => ({
    name: r.name,
    description: r.description || "",
  }));

  const acls = safeArray(data.acl_changes || data.acls_needed).map((a) => ({
    role: a.role || a.role_name,
    table_name: a.table_name,
    allowed_methods: safeArray(a.allowed_methods),
  }));

  const businessRules = safeArray(data.business_rules || data.business_rules_needed).map((br) => ({
    name: br.name,
    table_name: br.table_name,
    on_insert: Boolean(br.on_insert),
    on_update: Boolean(br.on_update),
    on_delete: Boolean(br.on_delete),
  }));

  const scheduledJobs = safeArray(data.scheduled_jobs || data.scheduled_jobs_needed).map((job) => ({
    name: job.name,
    frequency: job.frequency,
    execution_time: job.execution_time,
  }));

  const uiActions = safeArray(data.ui_actions_changes || data.ui_actions_needed).map((a) => ({
    name: a.name,
    label: a.label || a.name,
    table_name: a.table_name,
    script: a.script || "",
  }));

  return { tables, roles, acls, businessRules, scheduledJobs, uiActions };
}

function triggerLabel(rule) {
  const events = [];
  if (rule.on_insert) events.push("created");
  if (rule.on_update) events.push("updated");
  if (rule.on_delete) events.push("deleted");
  return events.length ? `when ${events.join("/")}` : "on change";
}

function frequencyLabel(job) {
  if (job.frequency && job.execution_time) return `${job.frequency} at ${job.execution_time}`;
  return job.frequency || "on a schedule";
}

function methodsLabel(methods) {
  const verbs = { GET: "view", POST: "create", PUT: "edit", DELETE: "delete" };
  const mapped = methods.map((m) => verbs[m] || m.toLowerCase());
  return mapped.length ? `can ${mapped.join(", ")}` : "";
}

export function buildFlowGraph(planState) {
  const { tables, roles, acls, businessRules, scheduledJobs, uiActions } = planState;

  const nodes = [];
  const edges = [];
  let edgeSeq = 0;
  const nextEdgeId = () => `edge-${edgeSeq++}`;

  const tableNodeId = (name) => `table:${name}`;
  const roleNodeId = (name) => `role:${name}`;

  tables.forEach((table) => {
    nodes.push({
      id: tableNodeId(table.name),
      type: "table",
      data: { label: table.label || table.name, fields: table.fields },
      position: { x: 0, y: 0 },
    });
  });

  roles.forEach((role) => {
    nodes.push({
      id: roleNodeId(role.name),
      type: "role",
      data: { label: role.name, description: role.description },
      position: { x: 0, y: 0 },
    });
  });

  acls.forEach((acl, idx) => {
    if (!acl.role || !acl.table_name) return;
    // Roles/tables aren't always both present yet mid-build (e.g. ACLs step
    // ran before roles resolved) — only draw the edge once both ends exist,
    // so the diagram never references a node that isn't there.
    const hasRole = roles.some((r) => r.name === acl.role);
    const hasTable = tables.some((t) => t.name === acl.table_name);
    if (!hasRole || !hasTable) return;

    edges.push({
      id: nextEdgeId(),
      source: roleNodeId(acl.role),
      target: tableNodeId(acl.table_name),
      label: methodsLabel(acl.allowed_methods),
      type: "smoothstep",
    });
  });

  businessRules.forEach((rule, idx) => {
    const id = `br:${rule.name || idx}`;
    nodes.push({
      id,
      type: "businessRule",
      data: { label: rule.name || "Automatic rule", subtitle: triggerLabel(rule) },
      position: { x: 0, y: 0 },
    });
    if (rule.table_name && tables.some((t) => t.name === rule.table_name)) {
      edges.push({
        id: nextEdgeId(),
        source: tableNodeId(rule.table_name),
        target: id,
        label: triggerLabel(rule),
        type: "smoothstep",
      });
    }
  });

  scheduledJobs.forEach((job, idx) => {
    const id = `job:${job.name || idx}`;
    nodes.push({
      id,
      type: "scheduledJob",
      data: { label: job.name || "Scheduled job", subtitle: frequencyLabel(job) },
      position: { x: 0, y: 0 },
    });
  });

  uiActions.forEach((action, idx) => {
    const id = `action:${action.name || idx}`;
    const sendsEmail = EMAIL_PATTERN.test(action.script || "");
    nodes.push({
      id,
      type: "uiAction",
      data: { label: action.label || action.name || "Button", subtitle: "button on the form" },
      position: { x: 0, y: 0 },
    });
    if (action.table_name && tables.some((t) => t.name === action.table_name)) {
      edges.push({
        id: nextEdgeId(),
        source: tableNodeId(action.table_name),
        target: id,
        label: "button on form",
        type: "smoothstep",
      });
    }
    if (sendsEmail) {
      const emailId = `email:${action.name || idx}`;
      nodes.push({
        id: emailId,
        type: "email",
        data: { label: "Email sent" },
        position: { x: 0, y: 0 },
      });
      edges.push({ id: nextEdgeId(), source: id, target: emailId, label: "sends", type: "smoothstep" });
    }
  });

  return layoutGraph(nodes, edges);
}

const NODE_WIDTH = 220;
const NODE_HEIGHT = 84;

function layoutGraph(nodes, edges, direction = "LR") {
  if (nodes.length === 0) return { nodes, edges };

  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: direction, nodesep: 40, ranksep: 90 });

  nodes.forEach((node) => {
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });
  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  const layoutedNodes = nodes.map((node) => {
    const pos = g.node(node.id);
    return {
      ...node,
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
    };
  });

  return { nodes: layoutedNodes, edges };
}
